// El ENTORNO del Chat: quién está logueado, su empresa y sus catálogos, y los tres
// contextos que las tarjetas del motor leen — ADR-2026-09-08-el-chat-toma-el-motor-del-asistente.
//
// Las tarjetas de `ChatPanelV2` (elegir producto, generar imagen/video, últimas
// generaciones, soporte) leen `ProductsContext`, `ModelsContext` y `AgentEnvContext`, que
// hasta ahora proveía `InterfazV2` para su propio chat. El Chat es GLOBAL —vive arriba de
// las rutas— así que necesita los suyos: acá se resuelven usuario, empresa, nombre corto
// y nombre de la empresa (las mismas tres consultas que hace `InterfazV2`), se cargan
// productos y modelos, y se proveen.
//
// CORTE POR CUENTA. El provider del chat se monta con `key` = id del usuario: cambiar
// de cuenta en la misma pestaña lo remonta de cero. Antes de ese remontaje se limpian
// las claves de sesión del Chat, porque el motor RESTAURA sus mensajes desde
// `sessionStorage` al montar y sin esto la conversación de la cuenta anterior
// reaparecía en la siguiente (ADR-2026-08-17-chat-atlas-corte-por-cuenta, que esto
// conserva con otro mecanismo).
//
// Ese corte "en vivo" no alcanza solo: el logout se va en una navegación DURA y puede
// llevarse el render que limpia. Por eso lo guardado además lleva DUEÑO estampado y se
// compara antes de restaurar — `sesionDelChat.ts`
// (ADR-2026-09-09-el-chat-guardado-lleva-dueno).

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { supabase } from '../lib/supabase';
import { resolveMarcaActiva, type MarcaActiva } from '../utils/marcaActiva';
import { AgentEnvContext } from '../pages/v2/agentEnvContext';
import type { MockProductV2 } from '../pages/v2/mockData';
import { ModelsContext } from '../pages/v2/modelsContext';
import { fetchUserModels, type ModelV2 } from '../pages/v2/modelsApi';
import { fetchUserProducts } from '../pages/v2/productsApi';
import {
  ProductEditContext,
  ProductsContext,
  ProductsLoadedContext,
} from '../pages/v2/productsContext';
import { vaciarRegistroDePdfs } from '../pages/v2/pdfs/pdfsEnMemoria';
import { vaciarRegistroDePlanillas } from '../pages/v2/planillas/planillasEnMemoria';
import { ChatAtlasProvider } from './ChatAtlasContext';
import { EntornoContext, useEntornoChatAtlas, type EntornoChatAtlas } from './entornoContext';
import { PuenteDePantallaProvider } from './PuenteDePantallaContext';
import { asegurarDuenoDeLaSesion, limpiarSesionDelChat } from './sesionDelChat';

// El contexto, su tipo y el hook viven en `entornoContext.ts`, una hoja. Y NO se
// re-exportan desde acá a propósito: este archivo importa `ChatAtlasProvider`, así que
// un re-export volvería a cerrar el ciclo que rompía el chat en desarrollo
// (ADR-2026-09-13-el-contexto-del-entorno-en-su-propio-modulo). Se piden a
// `entornoContext` (adentro de `chat-atlas`) o al barril (afuera).

// El corte por cuenta de lo guardado vive en `sesionDelChat.ts` (puro y con tests): la
// limpieza en vivo de acá no alcanza porque el logout se va en una navegación dura.
export { limpiarSesionDelChat } from './sesionDelChat';

/** Estado inicial y de "esta cuenta no tiene marcas": no filtra y no asocia nada. */
const SIN_MARCA: MarcaActiva = { id: null, rubro: null, variasMarcas: false };

const EntornoChatAtlasProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [usuarioId, setUsuarioId] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [shortName, setShortName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [plan, setPlan] = useState('');
  /**
   * La marca activa, AL DÍA — ADR-2026-09-13-el-cambio-de-marca-no-llega-al-chat.
   *
   * Antes se leía `profiles.current_brand_id` en el efecto del perfil y ahí se quedaba.
   * Eso fallaba dos veces: el campo viene `null` en las cuentas que nunca cambiaron de
   * marca (el Chat creía "sin marca" donde el resto de la app ya había elegido una), y como
   * este provider vive ARRIBA de `<Routes>`, ningún remonte lo alcanzaba — ni el `nonce` del
   * shell ni una navegación—: al cambiar de marca seguía pegado a la anterior, así que el
   * catálogo del agente y los productos que creaba eran de la marca vieja.
   *
   * Se resuelve con `resolveMarcaActiva` y NO con el hook `useRubroMarcaActiva`, aunque sea
   * el mismo desempate: ese hook resuelve una sola vez al montar, y este provider se monta
   * antes del login. Como el login es una navegación blanda (no recarga), el hook se
   * quedaría con el resultado de la sesión anónima para toda la sesión. Acá la resolución
   * la dispara `companyId` —que sí se re-resuelve al loguearse— más el evento.
   */
  const [marca, setMarca] = useState<MarcaActiva>(SIN_MARCA);
  const [marcaResuelta, setMarcaResuelta] = useState(false);
  const { id: brandId, rubro, variasMarcas } = marca;

  useEffect(() => {
    let vivo = true;
    // Cuál es la resolución vigente: dos cambios de marca seguidos disparan dos consultas,
    // y la que vuelve última no es necesariamente la última que se pidió. Sin esto, la
    // respuesta vieja pisa a la nueva y el chat queda en la marca que el usuario dejó.
    let ultima = 0;
    if (!companyId) {
      setMarca(SIN_MARCA);
      setMarcaResuelta(false);
      return;
    }
    const resolver = async () => {
      const mia = ++ultima;
      // Un cambio de marca invalida TODO lo de la anterior EN EL ACTO, antes de esperar a
      // que resuelva la nueva. Mientras dura la consulta el resto de la app ya cambió de
      // marca, y si acá se sigue ofreciendo la vieja, un mensaje mandado en esa ventana se
      // contesta con el catálogo equivocado y —peor— lo que el chat CREA queda colgado de
      // la marca que el usuario acaba de dejar. `SIN_MARCA` + `marcaResuelta: false` es el
      // estado honesto "todavía no sé cuál": el efecto de los catálogos espera, y lo que se
      // cree mientras tanto queda sin marca (huérfano, que se ve desde todas — ADR-018) en
      // vez de en la equivocada. En el arranque los dos ya están así: no cambia nada.
      setMarca(SIN_MARCA);
      setMarcaResuelta(false);
      try {
        const m = await resolveMarcaActiva(companyId);
        if (vivo && mia === ultima) setMarca(m);
      } catch {
        /* sin marca el Chat sigue andando; no filtra y no asocia */
      } finally {
        // En `finally`: si la consulta falla, dejar `marcaResuelta` en `false` para siempre
        // deja al catálogo esperando eternamente (el efecto de abajo lo espera), y un Chat
        // sin productos es peor que uno que no filtró.
        if (vivo && mia === ultima) setMarcaResuelta(true);
      }
    };
    void resolver();
    // El mismo evento que escucha el resto de la app: lo emiten el switch de marca y las
    // ediciones de nombre/rubro, que también cambian lo que se lee acá.
    window.addEventListener('atlas:brand-changed', resolver);
    return () => {
      vivo = false;
      window.removeEventListener('atlas:brand-changed', resolver);
    };
  }, [companyId]);

  const [productos, setProductos] = useState<MockProductV2[]>([]);
  const [productosCargados, setProductosCargados] = useState(false);
  const [modelos, setModelos] = useState<ModelV2[]>([]);

  // ── Quién está logueado ─────────────────────────────────────────────────
  // `getSession` es lectura local (no pega a la red); el listener sigue los cambios de
  // cuenta. `TOKEN_REFRESHED` y compañía llegan con el MISMO usuario: no cambian nada.
  useEffect(() => {
    let vivo = true;
    void (async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (vivo) setUsuarioId(session?.user?.id ?? null);
      } catch {
        /* sesión ilegible: se queda sin usuario */
      }
    })();
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((evento, session) => {
      if (evento === 'SIGNED_OUT') {
        setUsuarioId(null);
        return;
      }
      const actual = session?.user?.id ?? null;
      if (actual) setUsuarioId((prev) => (prev === actual ? prev : actual));
    });
    return () => {
      vivo = false;
      subscription.unsubscribe();
    };
  }, []);

  // ── Perfil y empresa ────────────────────────────────────────────────────
  useEffect(() => {
    let vivo = true;
    if (!usuarioId) {
      setCompanyId(null);
      setShortName('');
      setCompanyName('');
      setPlan('');
      return;
    }
    void (async () => {
      try {
        const { data: profile } = await supabase
          .from('profiles')
          .select('name_short, id_company')
          .eq('id', usuarioId)
          .single();
        if (!vivo) return;
        const p = profile as {
          name_short?: string | null;
          id_company?: string | null;
        } | null;
        setShortName(p?.name_short ?? '');
        const idCompany = p?.id_company ?? null;
        setCompanyId(idCompany);
        if (!idCompany) return;
        const { data: company } = await supabase
          .from('company_profiles')
          .select('nombre, plan')
          .eq('id_company', idCompany)
          .single();
        if (!vivo) return;
        const c = company as { nombre?: string | null; plan?: string | null } | null;
        setCompanyName((c?.nombre ?? '') || '');
        setPlan((c?.plan ?? '') || '');
      } catch {
        /* sin perfil el agente saluda sin nombre, nada más */
      }
    })();
    return () => {
      vivo = false;
    };
  }, [usuarioId]);

  // ── Catálogos ───────────────────────────────────────────────────────────
  const cargarModelos = useCallback(async (cid: string): Promise<ModelV2[]> => {
    try {
      return await fetchUserModels(cid);
    } catch {
      return [];
    }
  }, []);

  /**
   * El catálogo es el de la MARCA ACTIVA, no el de toda la empresa —
   * ADR-2026-09-13-el-cambio-de-marca-no-llega-al-chat.
   *
   * `fetchUserProducts` con marca devuelve los de esa marca **más los huérfanos**
   * (ADR-018), igual que `InterfazV2` y `MiMarca`. Sin el segundo argumento el agente veía
   * y ofrecía productos de las otras marcas de la cuenta —y los pickers,
   * `mostrar_guardado` y `ver_productos` con él, porque todos leen esta misma lista.
   */
  const cargarProductos = useCallback(
    async (cid: string, marcaId: string | null): Promise<MockProductV2[]> => {
      try {
        return await fetchUserProducts(cid, marcaId);
      } catch {
        return [];
      }
    },
    [],
  );

  useEffect(() => {
    let vivo = true;
    if (!companyId) {
      setProductos([]);
      setProductosCargados(false);
      setModelos([]);
      return;
    }
    // Se espera a que la marca resuelva: con `brandId` todavía en `null`,
    // `fetchUserProducts` no filtra y el catálogo entero de la empresa entraría al prompt
    // por un instante — y al agente le alcanza un turno para ofrecer lo que no debía.
    // `marcaResuelta` en `true` con `brandId` en `null` sí es final (cuenta sin marcas) y
    // ahí no filtrar es lo correcto.
    //
    // Y mientras resuelve, el catálogo de la marca ANTERIOR se vacía: dejarlo puesto es lo
    // que hacía que un mensaje mandado durante un cambio de marca se contestara con los
    // productos de la vieja. `productosCargados` en `false` es "todavía cargando" para los
    // pickers, no "esta marca no tiene productos".
    if (!marcaResuelta) {
      setProductos((previos) => (previos.length > 0 ? [] : previos));
      setProductosCargados(false);
      return;
    }
    void (async () => {
      const [prods, mods] = await Promise.all([
        cargarProductos(companyId, brandId),
        cargarModelos(companyId),
      ]);
      if (!vivo) return;
      setProductos(prods);
      setProductosCargados(true);
      setModelos(mods);
      firmadoEnRef.current = Date.now();
    })();
    return () => {
      vivo = false;
    };
  }, [companyId, brandId, marcaResuelta, cargarModelos, cargarProductos]);

  const refrescarModelos = useCallback(() => {
    if (!companyId) return;
    void cargarModelos(companyId).then(setModelos);
  }, [companyId, cargarModelos]);

  /**
   * Las fotos de productos y modelos van con links FIRMADOS de 1 hora (buckets privados) y el
   * catálogo se firmaba una sola vez, al cargar: con la pestaña abierta más de una hora, las
   * miniaturas de los pickers y de las tarjetas se rompían. Se vuelven a firmar solas pasados
   * 45 minutos —se mira cada 5 y al volver a la pestaña—, antes de que venzan.
   */
  const firmadoEnRef = useRef(0);

  useEffect(() => {
    if (!companyId || !marcaResuelta) return;
    const REFIRMAR_PASADOS_MS = 45 * 60 * 1000;
    // Como la carga inicial: si la marca cambió mientras se re-firmaba, el resultado es de la
    // marca VIEJA y no pisa el catálogo de la nueva (review atlas-code#179).
    let vivo = true;
    const refirmarSiVence = () => {
      if (!firmadoEnRef.current || Date.now() - firmadoEnRef.current < REFIRMAR_PASADOS_MS) return;
      firmadoEnRef.current = Date.now();
      void Promise.all([cargarProductos(companyId, brandId), cargarModelos(companyId)]).then(([prods, mods]) => {
        if (!vivo) return;
        setProductos(prods);
        setModelos(mods);
      });
    };
    const id = window.setInterval(refirmarSiVence, 5 * 60 * 1000);
    const alVolver = () => {
      if (document.visibilityState === 'visible') refirmarSiVence();
    };
    document.addEventListener('visibilitychange', alVolver);
    return () => {
      vivo = false;
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', alVolver);
    };
  }, [companyId, brandId, marcaResuelta, cargarModelos, cargarProductos]);

  // Tras crear productos desde una tarjeta (ADR-2026-09-08-el-chat-trabaja-en-lote): el
  // catálogo del prompt y de los pickers tiene que verlos en el turno siguiente.
  const refrescarProductos = useCallback(() => {
    if (!companyId) return;
    void cargarProductos(companyId, brandId).then(setProductos);
  }, [companyId, brandId, cargarProductos]);

  const valor = useMemo<EntornoChatAtlas>(
    () => ({
      usuarioId,
      companyId,
      shortName,
      companyName,
      plan,
      brandId,
      rubro,
      marcaResuelta,
      variasMarcas,
      productos,
      modelos,
      refrescarModelos,
      refrescarProductos,
    }),
    [
      usuarioId,
      companyId,
      shortName,
      companyName,
      plan,
      brandId,
      rubro,
      marcaResuelta,
      variasMarcas,
      productos,
      modelos,
      refrescarModelos,
      refrescarProductos,
    ],
  );

  const agentEnv = useMemo(
    () => ({
      userId: usuarioId,
      companyId,
      shortName,
      companyName,
      refreshModels: refrescarModelos,
      plan,
      brandId,
      variasMarcas,
    }),
    [
      usuarioId,
      companyId,
      shortName,
      companyName,
      refrescarModelos,
      plan,
      brandId,
      variasMarcas,
    ],
  );

  // Lo que los pickers y la tarjeta de carga de productos leen para refrescar el catálogo
  // (el mismo contexto que provee `InterfazV2` para su chat).
  const productEdit = useMemo(
    () => ({ companyId, refreshProducts: refrescarProductos }),
    [companyId, refrescarProductos],
  );

  return (
    <EntornoContext.Provider value={valor}>
      <ProductsContext.Provider value={productos}>
        <ProductsLoadedContext.Provider value={productosCargados}>
          <ProductEditContext.Provider value={productEdit}>
            <ModelsContext.Provider value={modelos}>
              <AgentEnvContext.Provider value={agentEnv}>{children}</AgentEnvContext.Provider>
            </ModelsContext.Provider>
          </ProductEditContext.Provider>
        </ProductsLoadedContext.Provider>
      </ProductsContext.Provider>
    </EntornoContext.Provider>
  );
};

/**
 * Los PDF y planillas adjuntos viven en un `Map` a nivel de MÓDULO, que no se desmonta
 * con el provider ni se va con una navegación de cliente: al cambiar de cuenta hay que
 * tirarlos a mano o los documentos del que se fue se quedan en la memoria de la pestaña
 * (ADR-2026-09-09-los-documentos-en-memoria-se-liberan). Acá sí se vacía ENTERO —a
 * diferencia del chat nuevo, que suelta sólo lo suyo—: de la cuenta anterior no queda
 * nada que valga la pena conservar.
 */
function vaciarDocumentosEnMemoria(): void {
  vaciarRegistroDePdfs();
  vaciarRegistroDePlanillas();
}

/** El provider del chat, remontado por usuario (ver el corte por cuenta arriba). */
const ProviderPorUsuario: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { usuarioId } = useEntornoChatAtlas();
  const previoRef = useRef<string | null>(null);
  // La limpieza corre DURANTE el render y no en un efecto, a propósito: el `key` nuevo
  // remonta al provider en este mismo render, y su `useState` inicial lee
  // `sessionStorage` en ese momento — un efecto llegaría tarde. Es idempotente, así que
  // el doble render de StrictMode no molesta. Del primer resolve (null → id) no se
  // limpia nada: no es un cambio de cuenta, es la cuenta llegando.
  if (previoRef.current !== null && previoRef.current !== usuarioId) {
    limpiarSesionDelChat();
    vaciarDocumentosEnMemoria();
  }
  // Y el primer resolve tampoco es gratis: lo que hay guardado puede ser de la cuenta
  // ANTERIOR de esta misma pestaña, si el logout se fue en la navegación dura antes de
  // que corriera la limpieza de arriba. El dueño estampado lo dice sin depender de eso.
  previoRef.current = usuarioId;
  if (asegurarDuenoDeLaSesion(usuarioId)) vaciarDocumentosEnMemoria();
  return <ChatAtlasProvider key={usuarioId ?? 'anon'}>{children}</ChatAtlasProvider>;
};

/** Lo que monta `App.tsx`: entorno + provider. */
export const ChatAtlasRaiz: React.FC<{ children: ReactNode }> = ({ children }) => (
  <EntornoChatAtlasProvider>
    {/* El registro de la pantalla con campos, por encima de las rutas y del chat
        (ADR-2026-09-10-el-agente-toca-la-pantalla). */}
    <PuenteDePantallaProvider>
      <ProviderPorUsuario>{children}</ProviderPorUsuario>
    </PuenteDePantallaProvider>
  </EntornoChatAtlasProvider>
);
